// The calendar as one reader meets it (PLAN-2026-10-30-the-calendar §4,
// scenario D12): the league's times (`leagueAgenda`) with the reader's own
// laid over them. Pure, so the endpoint and the practice league's simulator
// build the same calendar from their own reads.
//
// THE READER'S OWN TIMES ARE THE READS' THAT SERVE THEM, never worked out
// again here: a player's kickoff and status are his lineup row's, a week's
// problems its lineup health's, a claim's run its `processesAt`, a trade's
// moments its own. This only places them.

import {
  CALENDAR_EVENT_ORDER,
  type CalendarEvent,
  type CalendarPlayer,
  type CalendarView,
  type CalendarWeek,
  type ClaimView,
  type LineupProblem,
  type LineupRowView,
  type LineupView,
  type PlayerSummary,
  type TeamRef,
  type TradeSideView,
  type TradeView,
} from '@fsp/domain';
import type { LeagueAgenda } from './agenda';

/** A vote closing: a rule change, or a commissioner election. */
export interface CalendarVote {
  vote: 'rules' | 'election';
  at: string;
  text: string;
  voted: boolean;
}

/** A week's pick'em, while it is open. */
export interface CalendarPickem {
  period: number;
  /** When every pick locks. */
  at: string;
  picked: number;
  of: number;
}

/** What the reader brings: everything of theirs that happens at a time. */
export interface CalendarMine {
  /** My team. */
  teamId: string;
  /** My lineup for each week of the range the league plays, as the lineup read serves it. */
  lineups: readonly LineupView[];
  /** Whom I play each week, by period, and in a week of two games (Q6) my second. */
  opponents: ReadonlyMap<
    number,
    {
      opponent: TeamRef | null;
      matchupId: string | null;
      second?: { opponent: TeamRef | null; matchupId: string };
    }
  >;
  /** The trades the trades read serves me: offers to me, mine, and those in review. */
  trades: readonly TradeView[];
  /** My pending claims. */
  claims: readonly ClaimView[];
  votes: readonly CalendarVote[];
  pickem: readonly CalendarPickem[];
  /** When keepers lock, before a keeper or dynasty league's draft; null otherwise. */
  keepersAt: string | null;
}

export interface CalendarInput {
  agenda: LeagueAgenda;
  /** Null for a reader with no team: a watcher, or a commissioner with no seat. */
  mine: CalendarMine | null;
  /** Inclusive, in milliseconds. */
  from: number;
  /** Exclusive. */
  to: number;
}

/**
 * The lineup's problems that cost points, which the calendar says on their
 * week: what scores nothing at lock, as the lineup warning has it (B-233).
 */
export const CALENDAR_PROBLEMS: ReadonlySet<LineupProblem['code']> = new Set([
  'empty-slot',
  'starter-out',
  'starter-bye',
]);

/** A claim and the run it is decided at are one instant; within a minute, as a run is planned. */
const SAME_RUN_MS = 60_000;

const ms = (at: string): number => Date.parse(at);

/** The rows that can score this week: starters and the bench, never reserve or taxi. */
function scoringRows(view: LineupView): LineupRowView[] {
  return [...view.starters, ...view.bench];
}

/** A row's player at his kickoff; none for an empty slot, a bye, or a game with no time. */
function playerOf(row: LineupRowView): { at: string; player: CalendarPlayer } | null {
  const game = row.game;
  if (row.player === null || game === null || game.startAt === null) return null;
  if (game.state !== 'pre' && game.state !== 'live' && game.state !== 'final') return null;
  const player: CalendarPlayer = {
    player: row.player,
    slotKind: row.slotKind === 'starter' ? 'starter' : 'bench',
    slot: row.slot,
    opponent: game.opponent === null ? null : `${game.home ? 'vs' : 'at'} ${game.opponent}`,
    state: game.state,
    points: game.state === 'pre' ? null : row.points,
    health: row.health,
  };
  return { at: game.startAt, player };
}

/** "Hunt and a 2027 round 2 pick": what one side of a trade gives. */
function gives(side: TradeSideView): string {
  const names = [
    ...side.players.map((p) => p.player.name),
    ...side.draftPicks.map((p) => `a ${String(p.season)} round ${String(p.round)} pick`),
  ];
  if (names.length === 0) return 'nothing';
  if (names.length === 1) return names[0]!;
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]!}`;
}

/** A trade's moment on the calendar, or none for one with nothing still to come. */
function tradeEvent(t: TradeView, myTeamId: string): CalendarEvent | null {
  const [offered, other] = t.sides;
  const mine = t.isMine;
  const myIndex = t.sides.findIndex((s) => s.team.id === myTeamId);
  // FROM MY SIDE when it is mine: what I get for what I give.
  const summary =
    mine && myIndex >= 0
      ? `${gives(t.sides[1 - myIndex]!)} for your ${gives(t.sides[myIndex]!)}`
      : `${offered.team.name}'s ${gives(offered)} for ${other.team.name}'s ${gives(other)}`;
  const team = mine && myIndex >= 0 ? t.sides[1 - myIndex]!.team : offered.team;
  const partner = mine ? null : other.team;
  const base = { kind: 'trade' as const, tradeId: t.id, team, partner, summary, mine };
  switch (t.status) {
    case 'proposed':
      // An offer between two others is theirs to answer, and not on my calendar.
      if (!mine) return null;
      return {
        ...base,
        at: t.respondBy,
        period: null,
        step: t.isProposer ? 'awaiting' : 'answer',
        canVote: false,
      };
    case 'review':
      return t.reviewEndsAt === null
        ? null
        : {
            ...base,
            at: t.reviewEndsAt,
            period: null,
            step: 'review',
            canVote: t.votes.canVote && !t.votes.iVoted,
          };
    case 'accepted':
      // Going through is the parties' moment; a trade between two others
      // was the league's to look at while it was in review.
      return !mine || t.executeAt === null
        ? null
        : { ...base, at: t.executeAt, period: null, step: 'executes', canVote: false };
    default:
      return null;
  }
}

export function calendarView(input: CalendarInput): Pick<CalendarView, 'weeks' | 'events'> {
  const { agenda, mine, from, to } = input;
  const inRange = (at: string) => ms(at) >= from && ms(at) < to;
  const weekAt = (at: string): number | null =>
    agenda.weeks.find((w) => ms(w.start) <= ms(at) && ms(at) < ms(w.end))?.period ?? null;
  const lineupOf = new Map((mine?.lineups ?? []).map((l) => [l.period.index, l]));
  const events: CalendarEvent[] = [];

  const weeks: CalendarWeek[] = agenda.weeks.map((w) => {
    const lineup = lineupOf.get(w.period);
    const vs = mine?.opponents.get(w.period);
    return {
      ...w,
      opponent: vs?.opponent ?? null,
      matchupId: vs?.matchupId ?? null,
      secondOpponent: vs?.second?.opponent ?? null,
      secondMatchupId: vs?.second?.matchupId ?? null,
      byes:
        lineup === undefined
          ? []
          : scoringRows(lineup)
              .filter((r) => r.player !== null && r.game?.state === 'bye')
              .map((r) => r.player!),
      problems:
        lineup === undefined
          ? []
          : lineup.health.problems.filter((p) => CALENDAR_PROBLEMS.has(p.code)),
    };
  });

  // MY PLAYERS, BY KICKOFF: one entry for each instant one of mine kicks off.
  for (const lineup of mine?.lineups ?? []) {
    const week = agenda.weeks.find((w) => w.period === lineup.period.index);
    const byKickoff = new Map<number, CalendarPlayer[]>();
    for (const row of scoringRows(lineup)) {
      const p = playerOf(row);
      if (p === null || !inRange(p.at)) continue;
      const at = ms(p.at);
      byKickoff.set(at, [...(byKickoff.get(at) ?? []), p.player]);
    }
    for (const [at, players] of byKickoff) {
      const when = new Date(at).toISOString();
      const main = week?.mainSlateAt ?? null;
      events.push({
        kind: 'games',
        at: when,
        period: lineup.period.index,
        // Starters first, each group in the lineup's order.
        players: [
          ...players.filter((p) => p.slotKind === 'starter'),
          ...players.filter((p) => p.slotKind === 'bench'),
        ],
        games: week?.slate.find((k) => ms(k.at) === at)?.games ?? 1,
        early: main !== null && at < ms(main),
        locks: agenda.perPlayerLocks,
      });
    }
  }

  // THE LEAGUE'S TIMES, with what of mine each one takes.
  const claims = mine?.claims ?? [];
  const placed = new Set<string>();
  for (const e of agenda.events) {
    switch (e.kind) {
      case 'lineup-lock': {
        const lineup = lineupOf.get(e.period);
        events.push({
          ...e,
          starters: mine === null ? null : (lineup?.starters.length ?? null),
        });
        break;
      }
      case 'waivers': {
        const decided = claims.filter(
          (c) => c.processesAt !== null && Math.abs(ms(c.processesAt) - ms(e.at)) < SAME_RUN_MS,
        );
        for (const c of decided) placed.add(c.id);
        events.push({
          ...e,
          claims: mine === null ? null : decided.map((c) => c.player),
          continuous: false,
        });
        break;
      }
      default:
        events.push(e);
    }
  }
  // A CLAIM DECIDED AT ITS OWN TIME: every claim in a league that decides
  // them as players clear, and any whose run is not one of the agenda's.
  const own = new Map<string, PlayerSummary[]>();
  for (const c of claims) {
    if (placed.has(c.id) || c.processesAt === null || !inRange(c.processesAt)) continue;
    const at = new Date(ms(c.processesAt)).toISOString();
    own.set(at, [...(own.get(at) ?? []), c.player]);
  }
  for (const [at, players] of own)
    events.push({
      kind: 'waivers',
      at,
      period: weekAt(at),
      claims: players,
      continuous: agenda.continuous,
    });

  if (mine !== null) {
    const seen = new Set<string>();
    for (const t of mine.trades) {
      if (seen.has(t.id)) continue;
      seen.add(t.id);
      const e = tradeEvent(t, mine.teamId);
      if (e !== null && inRange(e.at)) events.push({ ...e, period: weekAt(e.at) });
    }
    for (const v of mine.votes)
      if (inRange(v.at)) events.push({ kind: 'vote', ...v, period: weekAt(v.at) });
    for (const p of mine.pickem)
      if (inRange(p.at))
        events.push({ kind: 'pickem', at: p.at, period: p.period, picked: p.picked, of: p.of });
    if (mine.keepersAt !== null && inRange(mine.keepersAt))
      events.push({ kind: 'deadline', at: mine.keepersAt, period: null, what: 'keepers' });
  }

  events.sort(
    (a, b) => ms(a.at) - ms(b.at) || CALENDAR_EVENT_ORDER[a.kind] - CALENDAR_EVENT_ORDER[b.kind],
  );
  return { weeks, events };
}
